import React, { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ContinueButton from "./ContinueButton";
import { useTranslation } from "react-i18next";
import { useDeviceTypeWatcher } from "@/_helpers/PhoneNumberHelper";
import Cookies from "universal-cookie";

const toTelHref = (phoneNumber) =>
  `tel:+${String(phoneNumber || "")
    .replace(/[^\d]/g, "")
    .trim()}`;

const normalizeKey = (value) =>
  String(value || "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

const findValueByKeys = (source, keys) => {
  if (!source || typeof source !== "object") return null;

  const keyMap = new Map(
    Object.keys(source).map((key) => [normalizeKey(key), source[key]]),
  );

  for (const key of keys) {
    const lookupKey = normalizeKey(key);
    if (!keyMap.has(lookupKey)) continue;

    const value = keyMap.get(lookupKey);
    if (value !== null && value !== undefined && value !== "") {
      return value;
    }
  }

  return null;
};

const toNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const formatCurrency = (value) => {
  const amount = toNumber(value);
  if (amount === null) return "--";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const parseQuote = (quote, partnerDiscountFallback) => {
  const root = quote && typeof quote === "object" ? quote : {};
  const nestedQuote =
    root.quote && typeof root.quote === "object" ? root.quote : {};

  const installationCost =
    findValueByKeys(root, [
      "installationCost",
      "installCost",
      "onetimeinstallationcost",
      "oneTimeInstallationCost",
      "laborChargesTotal",
      "totalDueToday",
      "dueToday",
    ]) ??
    findValueByKeys(nestedQuote, [
      "installationCost",
      "installCost",
      "onetimeinstallationcost",
      "oneTimeInstallationCost",
      "laborChargesTotal",
      "totalDueToday",
      "dueToday",
    ]);
  const programCost =
    findValueByKeys(root, [
      "programCostBiWeekly",
      "biWeeklyProgramCost",
      "biweeklyProgramCost",
      "every2WeekProgramCost",
      "everyTwoWeekProgramCost",
      "programCost",
      "monthlyPrice",
      "monthlyAmount",
      "monthlyPayment",
      "pricePerMonth",
      "recurringAmount",
      "invoiceTotal",
    ]) ??
    findValueByKeys(nestedQuote, [
      "programCostBiWeekly",
      "biWeeklyProgramCost",
      "biweeklyProgramCost",
      "every2WeekProgramCost",
      "everyTwoWeekProgramCost",
      "programCost",
      "monthlyPrice",
      "monthlyAmount",
      "monthlyPayment",
      "pricePerMonth",
      "recurringAmount",
      "invoiceTotal",
    ]);

  const partnerReferralDiscount =
    findValueByKeys(root, [
      "partnerReferralDiscount",
      "partnerReferralDiscountAmount",
      "referralDiscount",
      "partnerDiscount",
      "partnerDiscountAmount",
      "discountAmount",
    ]) ??
    findValueByKeys(nestedQuote, [
      "partnerReferralDiscount",
      "partnerReferralDiscountAmount",
      "referralDiscount",
      "partnerDiscount",
      "partnerDiscountAmount",
      "discountAmount",
    ]) ??
    partnerDiscountFallback;

  return {
    installationCost,
    programCost,
    partnerReferralDiscount,
  };
};

const PartnerPricingInfo = ({
  loading,
  quote,
  onScheduleAppointment,
  partnerDiscountFallback,
  partnerName,
  handleUpdate
}) => {
  const { t } = useTranslation();
  const cookies = new Cookies();

  const { phoneNumber } = useDeviceTypeWatcher();
  const [isContinuing, setIsContinuing] = useState(false);
  const isLoading = loading ?? false;
  const quoteData = quote ?? null;
  const onSchedule = onScheduleAppointment || (() => {});
  const discountFallback = partnerDiscountFallback ?? null;
  const resolvedPartnerName = (partnerName || "{partner_name}").trim();
  const parsed = parseQuote(quoteData, discountFallback);
  const discountAmount = toNumber(parsed.partnerReferralDiscount);
  const hasPartnerReferralDiscount = discountAmount !== 0;
  const enrollmentCodeData = cookies.get("lsEnrollmentCode");
  const discountText =
    discountAmount === null
      ? "--"
      : `-${formatCurrency(Math.abs(discountAmount))}`;
  const callText = phoneNumber || "(855) 221-0171";
  const callHref = toTelHref(callText);

  const handleContinueClick = () => {
    setIsContinuing(true);
    onSchedule();
    let newCookieData = {
      ...enrollmentCodeData,
      discountAmount: discountAmount,
    };

    cookies.set("lsEnrollmentCode", newCookieData, {
      path: "/",
    });

    handleUpdate("PartnerPricing")
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 540,
        mx: "auto",
        px: { xs: 1.5, sm: 3 },
        mt: { xs: 1, sm: 1.5 },
      }}
    >
      <Box
        sx={{
          mt: { xs: 2, sm: 3 },
          py: { xs: 1.2, sm: 1.8 },
          px: { xs: 1, sm: 2 },
          border: "1px solid #d9d9d9",
          borderRadius: 1,
          backgroundColor: "#fff",
          boxShadow: "0 2px 7px rgba(0, 0, 0, 0.12)",
          overflow: "hidden",
        }}
      >
        <Grid container>
          <Grid
            size={{ xs: 6 }}
            sx={{
              textAlign: "center",
            }}
          >
            <Typography sx={{ color: "#7a7a7a", fontSize: { xs: 14, sm: 14 } }}>
              {t("partnerPricing.installationCostLabel")}
            </Typography>
            <Typography
              sx={{
                mt: 0.4,
                color: "#4f9edb",
                fontWeight: 600,
                fontSize: { xs: 26, sm: 32 },
                lineHeight: 1.1,
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                formatCurrency(parsed.installationCost)
              )}
            </Typography>
          </Grid>

          <Grid
            size={{ xs: 6 }}
            sx={{
              textAlign: "center",
              borderLeft: "2px solid #d6d6d6",
            }}
          >
            <Typography sx={{ color: "#7a7a7a", fontSize: { xs: 14, sm: 14 } }}>
              {t("partnerPricing.programCostLabel")}
            </Typography>
            <Typography
              sx={{
                mt: 0.4,
                color: "#4f9edb",
                fontWeight: 600,
                fontSize: { xs: 26, sm: 32 },
                lineHeight: 1.1,
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                formatCurrency(parsed.programCost)
              )}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: { xs: 2.2, sm: 3.2 } }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: 18, sm: 18 },
            color: "#1a1a1a",
          }}
        >
          {t("partnerPricing.pricingOverviewTitle")}
        </Typography>

        {hasPartnerReferralDiscount && (
          <Box
            sx={{
              mt: 1.3,
              backgroundColor: "#eefbe2",
              px: { xs: 1, sm: 1.2 },
              py: { xs: 0.9, sm: 1.1 },
            }}
          >
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: { xs: 2, sm: 3.5 },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: 16, sm: 16 },
                    color: "#101010",
                    lineHeight: 1.25,
                  }}
                >
                  {t("partnerPricing.partnerReferralDiscountTitle")}
                </Typography>
                <Typography
                  sx={{
                    color: "#59605d",
                    fontSize: { xs: 14, sm: 14 },
                    lineHeight: 1.45,
                    maxWidth: { xs: "340px" },
                  }}
                >
                  {t("partnerPricing.partnerReferralDiscountDescription", {
                    partnerName: resolvedPartnerName,
                  })}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: 16, sm: 16 },
                  color: "#101010",
                  lineHeight: 1.25,
                  flexShrink: 0,
                  pl: { xs: 0.5, sm: 1 },
                }}
              >
                {isLoading ? "--" : discountText}
              </Typography>
            </Grid>
          </Box>
        )}

        <Box sx={{ mt: 2.2 }}>
          <Grid
            sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: 16, sm: 16 },
                color: "#171717",
              }}
            >
              {t("partnerPricing.oneTimeInstallationCostTitle")}
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: 16, sm: 16 },
                color: "#171717",
              }}
            >
              {isLoading ? "--" : formatCurrency(parsed.installationCost)}
            </Typography>
          </Grid>
          <Typography
            sx={{
              mt: 0.35,
              color: "#5f6567",
              fontSize: { xs: 14, sm: 14 },
              lineHeight: 1.45,
              maxWidth: { xs: "225px", sm: "340px" },
            }}
          >
            {t("partnerPricing.oneTimeInstallationCostDescription")}
          </Typography>
        </Box>

        <Box sx={{ mt: 2.2 }}>
          <Grid
            sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: 16, sm: 16 },
                color: "#171717",
              }}
            >
              {t("partnerPricing.programCostBiWeeklyTitle")}
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: 16, sm: 16 },
                color: "#171717",
              }}
            >
              {isLoading ? "--" : formatCurrency(parsed.programCost)}
            </Typography>
          </Grid>
          <Typography
            sx={{
              mt: 0.35,
              color: "#5f6567",
              fontSize: { xs: 14, sm: 14 },
              lineHeight: 1.45,
              maxWidth: { xs: "300px", sm: "340px" },
            }}
          >
            {t("partnerPricing.programCostBiWeeklyDescription")}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: { xs: 3.2, sm: 3.8 }, mb: 8, textAlign: "center" }}>
        <Typography
          sx={{
            color: "#5d5d5d",
            fontSize: { xs: 14, sm: 14 },
            lineHeight: 1.45,
          }}
        >
          {t("partnerPricing.questionsBeforeAppointment")}
        </Typography>
        <Typography
          sx={{
            color: "#5d5d5d",
            fontSize: { xs: 14, sm: 14 },
            lineHeight: 1.45,
          }}
        >
          {t("partnerPricing.questionsHelpPrefix")}{" "}
          <a
            href={callHref}
            style={{ color: "#4f9edb", textDecoration: "underline" }}
          >
            {callText}
          </a>
          {t("partnerPricing.questionsHelpSuffix")}
        </Typography>
      </Box>

      <Box sx={{ mt: { xs: 2.8, sm: 3.4 }, mb: { xs: 1, sm: 0 } }}>
        <ContinueButton
          submitting={isContinuing}
          onClick={handleContinueClick}
          continueText="partnerPricing.scheduleAppointmentCta"
          continueButtonShow
          isLongWidth
          // isStatic
          isFullWidth
        />
      </Box>
    </Box>
  );
};

PartnerPricingInfo.propTypes = {
  loading: PropTypes.bool,
  quote: PropTypes.object,
  onScheduleAppointment: PropTypes.func,
  partnerDiscountFallback: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  partnerName: PropTypes.string,
  handleUpdate:PropTypes.func,
};

export default PartnerPricingInfo;
